import { useEffect, useMemo, useState, type FunctionComponent } from "react";
import { Alert, DatePicker, Descriptions, Form, InputNumber, message, Modal, Radio, Select, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { productClientActions } from "@fitness-app/app-store";
import {
  type ChangeInstallmentPlanSummary,
  type InstallmentPlanAccessEnd,
} from "@fitness-app/data-models/entities/InstallmentPlanChange";
import { PriceStatus } from "@fitness-app/data-models/entities/Product";
import { type SubscriptionProductClient } from "@fitness-app/data-models/entities/ProductClient";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";
import { getProductClientAccessEnd } from "@fitness-app/utils/src/products/getProductClientAccessEnd";

import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface OwnProps {
  open: boolean;
  onClose: () => void;
  productClient: SubscriptionProductClient;
}

type PlanSource = "price" | "manual";

interface FormModel {
  planSource: PlanSource;
  /** Installment price picked from the product's price list. */
  newPriceId?: string;
  newInstallments?: number;
  /** In PLN — converted to grosze before hitting the API. */
  newInstallmentAmount?: number;
  /** Manual variant only — a price brings its own access duration. */
  accessEndMode: InstallmentPlanAccessEnd["mode"];
  accessEndsAt?: Dayjs | null;
}

const formatAmount = (amount: number, currency: string) =>
  `${(amount / 100).toFixed(2).replace(".", ",")} ${currency.toUpperCase()}`;

const formatDate = (unixSeconds: number) => dayjs.unix(unixSeconds).format("DD.MM.YYYY");

const toAccessEnd = (formData: FormModel): InstallmentPlanAccessEnd => {
  if (formData.accessEndMode === "followInstallments") {
    return { mode: "followInstallments" };
  }

  if (formData.accessEndMode === "custom" && formData.accessEndsAt) {
    // End of the selected day — access lasts through it, the same convention
    // as the extend-access modal.
    return { mode: "custom", endsAt: formData.accessEndsAt.endOf("day").unix() };
  }

  return { mode: "keepCurrent" };
};

/**
 * Admin change of the installment count/amount on an active installment order
 * (TRE-1764). The new plan comes either from an installment price already
 * defined on the product (count and amount from its definition, the remaining
 * installments trimmed by the ones already billed) or from numbers typed in by
 * hand. Two-step: a dry run computes the full money breakdown on the backend,
 * the admin confirms what she sees, and only then the schedule is rewritten.
 *
 * Access is a separate length from the billing. A price from the list carries
 * its own ("13 miesięcy, 2 raty"); typed-in numbers carry nothing, so the
 * manual variant asks explicitly and defaults to leaving access where it is
 * (TRE-1970). The confirmation always states the horizon before and after —
 * hiding the row when it matched the last installment was how a plan shortened
 * from twelve installments to two took ten months of access with it, unnoticed.
 */
const ChangeInstallmentPlanModal: FunctionComponent<OwnProps> = ({ open, onClose, productClient }) => {
  const dispatch = useAppDispatch();
  const product = useAppSelector((store) => store.product.details);
  const [formController] = Form.useForm<FormModel>();
  const [changeId, setChangeId] = useState(() => generateUniqId());
  const [summary, setSummary] = useState<ChangeInstallmentPlanSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const billedInstallments = productClient.accessPeriod.cycleNumber;
  const planSource = Form.useWatch("planSource", formController) ?? "price";
  const accessEndMode = Form.useWatch("accessEndMode", formController) ?? "keepCurrent";
  // Where access reaches today. Installments and access are two different
  // lengths ("13 miesięcy, 2 raty"), so the manual variant has to say out loud
  // what happens to this date — see the `accessEndMode` field below.
  const currentAccessEnd = getProductClientAccessEnd(productClient);

  // Installment prices of this product the plan can be switched to. A package
  // whose total does not exceed the billed installments cannot be picked — no
  // future installment would remain.
  const installmentPriceOptions = useMemo(
    () =>
      (product?.prices ?? [])
        .filter((price) => (price.installments ?? 0) > 1 && price.unit_amount && price.status !== PriceStatus.Archived)
        .map((price) => ({
          value: price.id,
          disabled: (price.installments ?? 0) <= billedInstallments,
          label:
            `${price.installments} rat × ${formatAmount(price.unit_amount ?? 0, price.currency)}` +
            (price.customLabel ? ` — ${price.customLabel}` : "") +
            ((price.installments ?? 0) <= billedInstallments ? " (opłacono już więcej rat)" : ""),
        })),
    [product?.prices, billedInstallments],
  );

  const hasPriceOptions = installmentPriceOptions.some((option) => !option.disabled);

  useEffect(() => {
    if (open) {
      // A fresh id per opened form: it is the idempotency key of ONE change.
      setChangeId(generateUniqId());
      setSummary(null);
      formController.resetFields();
    }
  }, [open, formController]);

  // `initialValues` is read once, and the price list arrives asynchronously
  // (the drawer fetches the product on mount). Without this the form could be
  // stuck on "Kwota ręczna" — the variant that cannot carry a price's
  // customDuration — for a product that does have installment prices.
  useEffect(() => {
    if (open) {
      formController.setFieldValue("planSource", hasPriceOptions ? "price" : "manual");
    }
  }, [open, hasPriceOptions, formController]);

  const runChange = async (dryRun: boolean) => {
    let formData: FormModel;
    try {
      formData = await formController.validateFields();
    } catch {
      // Ant Design already renders the field errors.
      return;
    }
    setLoading(true);

    try {
      const result = await dispatch(
        productClientActions.changeInstallmentPlan({
          productClientId: productClient.id,
          ...(formData.planSource === "price"
            ? { newPriceId: formData.newPriceId }
            : {
                newInstallments: formData.newInstallments,
                newInstallmentAmount: Math.round((formData.newInstallmentAmount ?? 0) * 100),
                accessEnd: toAccessEnd(formData),
              }),
          changeId,
          dryRun,
        }),
      ).unwrap();

      if (dryRun) {
        setSummary(result);
      } else {
        void message.success("Plan ratalny został zmieniony");
        void dispatch(
          productClientActions.fetchProductClient({
            productId: productClient.productId,
            clientEmail: productClient.email,
          }),
        );
        onClose();
      }
    } catch (e) {
      void message.error(typeof e === "string" ? e : "Nie udało się zmienić planu ratalnego");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOk = () => {
    void runChange(!summary);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Zmień plan ratalny"
      width={700}
      okText={summary ? "Potwierdź zmianę" : "Przelicz"}
      cancelText="Anuluj"
      onOk={handleOk}
      okButtonProps={{ loading, danger: Boolean(summary) }}
    >
      <Alert
        showIcon
        type="info"
        message="Zmiana liczby i kwoty rat na aktywnym zamówieniu"
        description={
          `Opłacone raty (${billedInstallments}) pozostają bez zmian. Różnica za opłacone raty zostanie ` +
          "doliczona (lub skredytowana) przy najbliższej fakturze, a pozostałe raty będą pobierane w nowej " +
          "kwocie. Zamówienie nie jest anulowane — klientka nie traci dostępu ani programu."
        }
        style={{ marginBottom: 16 }}
      />
      <Form<FormModel>
        form={formController}
        labelCol={{ span: 10 }}
        wrapperCol={{ span: 14 }}
        onValuesChange={() => setSummary(null)}
        initialValues={{
          planSource: hasPriceOptions ? "price" : "manual",
          newInstallments: productClient.accessPeriod.installments ?? undefined,
          accessEndMode: "keepCurrent",
        }}
      >
        <Form.Item label="Opłacone raty">
          <Typography.Text>
            {billedInstallments} / {productClient.accessPeriod.installments}
          </Typography.Text>
        </Form.Item>
        <Form.Item
          name="planSource"
          label="Nowy plan"
          extra={
            hasPriceOptions
              ? undefined
              : installmentPriceOptions.length === 0
                ? "Ten produkt nie ma cen ratalnych w cenniku — zostaje kwota ręczna."
                : `Żadna cena ratalna z cennika nie ma więcej niż ${billedInstallments} rat — zostaje kwota ręczna.`
          }
        >
          <Radio.Group
            options={[
              // Ten sam warunek co reszta formularza: cennik z samymi
              // niewybieralnymi pozycjami to wciąż brak wyboru, a pole ceny
              // jest wymagane.
              { value: "price", label: "Cennik produktu", disabled: !hasPriceOptions },
              { value: "manual", label: "Kwota ręczna" },
            ]}
          />
        </Form.Item>
        {planSource === "price" ? (
          <Form.Item
            name="newPriceId"
            label="Pakiet ratalny z cennika"
            extra="Liczba i kwota rat pochodzą z definicji ceny — razem z jej długością dostępu (np. „13 miesięcy, 2 raty”)."
            rules={[{ required: true, message: "Wybierz cenę ratalną" }]}
          >
            <Select options={installmentPriceOptions} placeholder="Wybierz pakiet" />
          </Form.Item>
        ) : (
          <>
            <Form.Item
              name="newInstallments"
              label="Nowa łączna liczba rat"
              rules={[
                { required: true, message: "Podaj liczbę rat" },
                {
                  type: "number",
                  min: billedInstallments + 1,
                  message: `Liczba rat musi być większa niż liczba opłaconych (${billedInstallments})`,
                },
                { type: "number", max: 48, message: "Maksymalna liczba rat to 48" },
              ]}
            >
              <InputNumber style={{ minWidth: 200 }} precision={0} />
            </Form.Item>
            <Form.Item
              name="newInstallmentAmount"
              label="Nowa kwota raty (zł)"
              rules={[
                { required: true, message: "Podaj kwotę raty" },
                { type: "number", min: 1, message: "Kwota raty musi wynosić co najmniej 1 zł" },
              ]}
            >
              <InputNumber style={{ minWidth: 200 }} precision={2} decimalSeparator="," />
            </Form.Item>
            {/* Liczba i kwota rat nie mówią nic o tym, jak długo trwa dostęp —
                pakiet może być rozliczony w 2 ratach i dawać 13 miesięcy. Bez
                tego pola skrócenie rat po cichu zabierało opłacone miesiące. */}
            <Form.Item
              name="accessEndMode"
              label="Dostęp po zmianie"
              extra="Zmiana liczby rat sama w sobie nie skraca ani nie wydłuża dostępu."
            >
              <Radio.Group
                options={[
                  {
                    value: "keepCurrent",
                    label: currentAccessEnd ? `Bez zmian — do ${formatDate(currentAccessEnd)}` : "Bez zmian",
                  },
                  { value: "followInstallments", label: "Do końca nowych rat" },
                  { value: "custom", label: "Własna data" },
                ]}
              />
            </Form.Item>
            {accessEndMode === "custom" ? (
              <Form.Item
                name="accessEndsAt"
                label="Dostęp do dnia"
                rules={[{ required: true, message: "Wybierz datę końca dostępu" }]}
              >
                <DatePicker
                  style={{ minWidth: 200 }}
                  format="DD.MM.YYYY"
                  showNow={false}
                  disabledDate={(current) => current.endOf("day").isBefore(dayjs())}
                />
              </Form.Item>
            ) : null}
          </>
        )}
      </Form>
      {summary ? (
        <Alert
          showIcon
          type="warning"
          message="Sprawdź i potwierdź zmianę"
          description={
            <Descriptions column={1} size="small" bordered labelStyle={{ width: 260 }}>
              <Descriptions.Item label="Obecny plan">
                {summary.previousInstallments} rat po{" "}
                {formatAmount(summary.previousInstallmentAmount, summary.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Nowy plan">
                {summary.newInstallments} rat po {formatAmount(summary.newInstallmentAmount, summary.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Opłacone raty">{summary.billedInstallments}</Descriptions.Item>
              <Descriptions.Item label="Wyrównanie przy najbliższej fakturze">
                {summary.adjustmentAmount === 0 ? "brak" : formatAmount(summary.adjustmentAmount, summary.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Pozostałe raty">
                {summary.remainingInstallments} × {formatAmount(summary.newInstallmentAmount, summary.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Koniec rat (szacunkowo)">
                {formatDate(summary.installmentEndsAt)}
              </Descriptions.Item>
              {/* Zawsze, nawet gdy pokrywa się z końcem rat: to jest ta liczba,
                  której nikt nie odtworzy z kwot po fakcie, a jej skrócenie jest
                  jedyną nieodwracalną częścią tej zmiany dla klientki. */}
              <Descriptions.Item label="Koniec dostępu">
                {summary.previousAccessEndsAt === summary.accessEndsAt ? (
                  <>{formatDate(summary.accessEndsAt)} (bez zmian)</>
                ) : (
                  <Typography.Text type={summary.accessEndsAt < summary.previousAccessEndsAt ? "danger" : "success"}>
                    {formatDate(summary.previousAccessEndsAt)} → {formatDate(summary.accessEndsAt)}
                    {summary.accessEndsAt < summary.previousAccessEndsAt ? " (skrócony)" : " (wydłużony)"}
                  </Typography.Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Suma pobrań po zmianie">
                {formatAmount(summary.newInstallments * summary.newInstallmentAmount, summary.currency)}
              </Descriptions.Item>
            </Descriptions>
          }
        />
      ) : null}
    </Modal>
  );
};

export default ChangeInstallmentPlanModal;
